import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { emit } from '../../platform/analytics/analytics'
import { runBulkEdit } from '../transactions/bulkEdit'
import type { BulkOutcome } from '../transactions/runBulk'
import type { Txn } from '../transactions/transactionWire'
import { tagListQuery } from '../transactions/useTransactions'
import { collidingTag, TagNameTakenError } from './tagRules'

export type TagChange = { name: string; updated: number }

export class TagChangeError extends Error {
  readonly status: number | undefined
  constructor(cause: unknown, status?: number) {
    super('tag-change-failed', { cause })
    this.name = 'TagChangeError'
    this.status = status
  }
}

/**
 * `.` and `..` are the two names no client can address: `encodeURIComponent` leaves them alone and the
 * browser resolves them as dot segments, so `/tags/..` becomes `/api/v1/transactions/` (405).
 */
export function canAddress(name: string): boolean {
  return name !== '.' && name !== '..'
}

/**
 * Every query that shows a tag: the transaction list, detail, the tag list and counts, summaries (all
 * under `transactions`), and Reports' rows-by-tag and tag filters (under `cashflow`). NOT `review`:
 * it renders no tags, and its key docblock forbids refetches that make rows jump.
 */
function invalidateTagViews(client: QueryClient): Promise<unknown> {
  return Promise.all([
    client.invalidateQueries({ queryKey: keys.transactions.all() }),
    client.invalidateQueries({ queryKey: keys.cashflow.all() }),
  ])
}

/** A call that never reached the server rejects with a bare `TypeError`; the dialog only needs to know it failed. */
async function call(run: () => Promise<{ data?: TagChange; error?: unknown; response: Response }>): Promise<TagChange> {
  let res: Awaited<ReturnType<typeof run>>
  try {
    res = await run()
  } catch (cause) {
    throw new TagChangeError(cause)
  }
  if (res.error !== undefined || res.data === undefined) throw new TagChangeError(res.error, res.response.status)
  return { name: res.data.name, updated: res.data.updated }
}

/**
 * Rename every transaction's `from` tag to `to`. The dialog pre-checks `to` against another tag (case-insensitively), and this hook
 * re-checks against a freshly fetched list before the PATCH: the backend would merge silently and never
 * answers 409, so there is no merge path to take. `networkMode: 'always'` — offline, the PATCH is attempted and rejects at once into
 * the dialog's error; the default would pause it on "Renaming…" indefinitely.
 */
export function useRenameTag() {
  const client = useQueryClient()
  return useMutation<TagChange, TagChangeError | TagNameTakenError, { from: string; to: string }>({
    networkMode: 'always',
    retry: false,
    mutationFn: async ({ from, to }) => {
      // The dialog's check reads a list that may be arbitrarily old (nothing refetches on focus), and a
      // merge cannot be undone — so re-check against a list fetched NOW, and send nothing on a hit.
      let fresh: readonly string[]
      try {
        fresh = await client.fetchQuery({ ...tagListQuery(), staleTime: 0, networkMode: 'always' })
      } catch (cause) {
        throw new TagChangeError(cause)
      }
      const existing = collidingTag(fresh, from, to)
      if (existing !== undefined) throw new TagNameTakenError(existing)
      return call(() => api.PATCH('/api/v1/transactions/tags/{name}', { params: { path: { name: from } }, body: { name: to } }))
    },
    onSuccess: async (result) => {
      await invalidateTagViews(client)
      emit('tag_renamed', { transactions_count: result.updated })
    },
    // A 404 means the list on screen was stale: the tag is already gone.
    onError: (error) =>
      error instanceof TagChangeError && error.status === 404 ? invalidateTagViews(client) : undefined,
  })
}

export function useDeleteTag() {
  const client = useQueryClient()
  return useMutation<TagChange, TagChangeError, string>({
    networkMode: 'always',
    retry: false,
    mutationFn: (name) => call(() => api.DELETE('/api/v1/transactions/tags/{name}', { params: { path: { name } } })),
    onSuccess: async (result) => {
      await invalidateTagViews(client)
      emit('tag_deleted', { transactions_count: result.updated })
    },
    onError: (error) => (error.status === 404 ? invalidateTagViews(client) : undefined),
  })
}

/**
 * Create a tag by putting it on `rows`: the backend has no standalone tags, a tag exists exactly while
 * a transaction carries it. The name is re-checked against a list fetched NOW (as `useRenameTag` does:
 * the on-screen list may be old, and an existing tag must never be silently extended under a new
 * spelling), then `runBulkEdit('add_tag')` appends it to each row's own tags with the bulk path's
 * concurrency, 50-tag cap and per-row failure reasons. Partial success resolves with the outcome — the
 * tag exists on `done` rows, so the dialog reports rather than throws. `networkMode: 'always'`: offline,
 * the fresh fetch is attempted and rejects at once; the default would park the mutation forever.
 */
export function useCreateTag() {
  const client = useQueryClient()
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const mutation = useMutation<BulkOutcome, TagChangeError | TagNameTakenError, { name: string; rows: readonly Txn[] }>({
    networkMode: 'always',
    retry: false,
    mutationFn: async ({ name, rows }) => {
      let fresh: readonly string[]
      try {
        fresh = await client.fetchQuery({ ...tagListQuery(), staleTime: 0, networkMode: 'always' })
      } catch (cause) {
        throw new TagChangeError(cause)
      }
      const existing = collidingTag(fresh, '', name)
      if (existing !== undefined) throw new TagNameTakenError(existing)
      try {
        return await runBulkEdit('add_tag', rows, name, (done, total) => setProgress({ done, total }))
      } finally {
        setProgress(null)
      }
    },
    onSuccess: async (outcome) => {
      await invalidateTagViews(client)
      if (outcome.done.length > 0) emit('tag_created', { transactions_count: outcome.done.length })
    },
  })
  return { ...mutation, progress }
}
